import { openModal } from "action/modal/openModal";
import getVimeoThumbnail from "api/product/getVimeoThumbnail";
import SliderV2 from "component/design-system/carousel/SliderV2";
import HorizontalSeparator from "component/design-system/horizontal-separator/HorizontalSeparator";
import PAImage from "component/design-system/images/PAImage";
import PAText, { FONT_SIZE } from "component/design-system/typography/PAText/PAText";
import RefreshIcon from "icons/RefreshIcon";
import { PackageIcon } from "modules/product/header/PackageIcon";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "modules/tabs-pill/TabsPill";
import dynamic from "next/dynamic";
import {
  forwardRef,
  useEffect,
  useRef, useState,
} from "react";
import { ReactImageTurntable } from "react-image-turntable";
import Skeleton from "react-loading-skeleton";
import { useDispatch } from "react-redux";
import HideIf from "util/HideIf";
import canUseWebpImage from "util/helper/canUseWebpImage";
import { getRelativeImagePath } from "util/helper/getRelativeImagePath";
import styles from "./mobile-product-image-slider.module.scss";

const SliderMainImageV2 = dynamic(() => import(/* webpackChunkName: "slider-main-image-v2" */ "./SliderMainImageV2"),
  { loading: () => <Skeleton height={450} count={1} /> });
const ForwardedRefSliderMainImageV2 = forwardRef((props, ref) => (
  <SliderMainImageV2 {...props} forwardedRef={ref} />
));

const MobileProductImage = (props) => {
  const {
    selectedImage, productImages, productImagesV2,
    dim,
    imageTitle,
    isPopUp,
    getProductImagePath,
    spinningImage,
    isHovered,
    updatedVideos,
    marketingImages,
    isFluidWidth,
    setCurrentImageIndex,
    maxThumbnailInViewport,
    setSelectedImage,
    zoomImagePosition,
    fromSideMenu,
    imagePreload,
    isWebpImage,
    handleImageClick,
    largeImage,
    getThumbImagePath,
    product,
  } = props;
  const mainSliderRefV2 = useRef(null);
  const dispatch = useDispatch();
  const [videoThumbnails, setVideoThumbnails] = useState<string[]>([]);
  const [selectedVideo, setSelectedVideo] = useState(0);

  const productImagesArr = isWebpImage && productImagesV2.length > 0 ? productImagesV2 : productImages;
  const productImageUrls = (productImagesArr.find((_: any) => _.type === "SINGLE_IMAGE")?.urls || []).filter((_: any) => _ !== "");
  const allProductImages = productImageUrls.map((image: string) => ({ img: image, zoomImg: image }));
  const initialImagePath = canUseWebpImage() ? "/img/zdb" : "/product-images";
  const showOnlyImages = updatedVideos.length === 0 && spinningImage.urls.length === 0;

  useEffect(() => {
    const fetchVideoThumbnails = async () => {
      const thumbnailPromises = updatedVideos.map(async (video) => {
        if (video.thumbnailUrl) {
          return getRelativeImagePath(`/cms/${video.thumbnailUrl}`);
        } if (video.youtubeId) {
          return `https://img.youtube.com/vi/${video.youtubeId}/sddefault.jpg`;
        } if (video.vimeoId) {
          const data = await getVimeoThumbnail(video.vimeoId);
          return data.thumbnail_url;
        }
        return "";
      });

      const thumbnails = await Promise.all(thumbnailPromises);
      setVideoThumbnails(thumbnails);
    };

    if (updatedVideos.length > 0) {
      fetchVideoThumbnails();
    }
  }, [updatedVideos]);

  const handleVideoClick = (videoIndex: number) => {
    dispatch(openModal("thumbnailVideo", {
      productImages,
      productImagesV2,
      getThumbImagePath,
      marketingImages,
      imageTitle,
      product,
      productLineVideos: updatedVideos,
      defaultSelectedVideo: videoIndex,
      isMobile: true,
    }));
  };

  return (
    <Tabs defaultValue="photos">

      {/* Tab Content */}
      <TabsContent value="photos">
        <div id="divImageContainer">

          <HideIf show={allProductImages.length > 0}>

            {/* https://partsavatar.ca/walker-products-idle-air-control-motor-215-91001 */}
            <ForwardedRefSliderMainImageV2
              ref={mainSliderRefV2}
              {...{
                selectedImage,
                productImages,
                productImagesV2,
                dim,
                imageTitle,
                isPopUp,
                getProductImagePath,
                spinningImage: {},
                isHovered,
                videosCount: updatedVideos.length,
              }}
              marketingImages={marketingImages}
              isFluidWidth={isFluidWidth}
              isDesktop={false}
              onArrowClick={(index) => {
                setCurrentImageIndex(maxThumbnailInViewport * Math.trunc(index / maxThumbnailInViewport));
                setSelectedImage(index);
              }}
              zoomImagePosition={zoomImagePosition}
              showDots={false}
              fromSideMenu={fromSideMenu}
              imageSize={imagePreload}
              isWebpImage={isWebpImage}
              handleDotClick={() => {}}
              handleImageClick={handleImageClick}
              largeImage={largeImage}
              showArrows={Boolean(isPopUp)}
            />
          </HideIf>
          {/* <FinalSaleTag product={product} className={styles.final_sale_tag} /> */}
          <PAText
            color="white"
            fontSize={FONT_SIZE[0]}
            className={styles.counter}
          >
            {`${selectedImage + 1} / ${allProductImages.length}`}
          </PAText>
        </div>
      </TabsContent>

      <TabsContent value="videos" className={styles.video_thumbnail_wrapper}>
        <div className={styles.video_thumbnail_inner_wrapper}>
          <PAText
            color="white"
            fontSize={FONT_SIZE[0]}
            className={styles.counter}
          >
            {`${selectedVideo + 1} / ${videoThumbnails.length}`}
          </PAText>
          <SliderV2
            showDots={false}
            showArrows
            ulClassName={styles.product_video_ul}
            onImageChange={(current) => {
              setSelectedVideo(current);
            }}
          >
            {videoThumbnails.map((thumbnailUrl: any, index: number) => (
              <div
                key={`video-${thumbnailUrl}`}
                className={styles.video_thumbnail}
                onClick={() => handleVideoClick(index)}
              >
                <PAImage
                  src={thumbnailUrl}
                  alt={thumbnailUrl || `Video ${index + 1}`}
                  width={300}
                  height={300}
                  className={styles.video_image}
                />
                <i className={`fa fa-play ${styles.play_button}`}> </i>
              </div>
            ))}
          </SliderV2>
        </div>
      </TabsContent>

      <TabsContent value="360" className="d-flex align-items-center">
        <div
          onClick={() => handleImageClick(allProductImages.length)}
          className="pointer"
        >
          <ReactImageTurntable
            images={spinningImage.urls.map((el: string) => getRelativeImagePath(`${initialImagePath}/${el}`))}
          />
        </div>
      </TabsContent>

      <TabsList>
        <HideIf show={!showOnlyImages}>
          <TabsTrigger value="photos">
            <PackageIcon />
            {`Photos (${allProductImages.length})`}
          </TabsTrigger>
        </HideIf>

        <HideIf show={updatedVideos.length > 0}>
          <TabsTrigger value="videos" icon={<i className="fa fa-play" />}>
            {`Videos (${videoThumbnails.length})`}
          </TabsTrigger>
        </HideIf>

        <HideIf show={spinningImage.urls.length > 0}>
          <TabsTrigger value="360">
            <RefreshIcon />
            360°
          </TabsTrigger>
        </HideIf>
      </TabsList>
      <HideIf show={!showOnlyImages}>
        <HorizontalSeparator className="m-b-0" />
      </HideIf>
    </Tabs>
  );
};

export default MobileProductImage;
